import { format } from 'date-fns';
import { put, race, select, take, takeLatest } from 'redux-saga/effects';
import {
  findClosingSalesRequested,
  reportFilterSelector,
  reportOpStructuresFilterSelector,
  userInfoSelector,
} from '..';
import { TablesExportPdf } from '../../exports';
import { getParametersTable } from '../../exports/helpers/getParametersTable';
import { Closing, OpStructuresFilter, ReportFilter, Translation } from '../../types';
import { enqueueNotification } from '../notifications/actionCreators';
import {
  FindClosingSalesFailureAction,
  FindClosingSalesSuccessAction,
  ActionType as ReportActionType,
} from '../report/types';
import { downloadSuccess, downloadFailure, downloadRequested } from './actionCreators';
import { ActionType, DownloadReportPdfAction, DownloadRequestedAction } from './types';

const getMissingsTableDates = (data: Closing[]) => [
  ...new Set(data.map(i => i.date.toISOString())),
];

const getMissingsData = (data: Closing[], t: Translation) => {
  const dict: Record<string, [string, string][]> = {};
  data.forEach(i => {
    i.cause.forEach(cause => {
      const date = i.date.toISOString();
      if (!dict[date]) dict[date] = [];
      dict[date].push([i.store, t(`missing-closing-detail.causes.${cause}`)]);
    });
  });
  return dict;
};

const addMissings = (
  tablesExport: TablesExportPdf,
  data: Closing[],
  t: Translation,
  locale: Locale
) => {
  const dates = getMissingsTableDates(data);
  const missingsData = getMissingsData(data, t);

  dates.forEach(date => {
    const titleDate = format(new Date(date), 'P', { locale });
    tablesExport.pushTable(date, {
      headers: [t('table.columns.store'), t('table.columns.cause')],
      rows: missingsData[date],
      title: t('shared.pdfExport.missingClosingDate', { date: titleDate }),
      columnConfig: [
        { width: 50, align: 'left' },
        { width: 200, align: 'left' },
      ],
    });
  });
};
/*
const data = [
  {
    header: t('dataGridFlexSales.exports.labels.parameterValue'),
    country: t(`operativeStructure.countries.${filters.countryCode}`),
    store: filters.stores.map(i => i.storeAcronym).join(', '),
    pos: filters.pos.join(', '),
    saleType: filters.salesTypes.map(i => i.saleTypeDescription).join(', '),
    segment: filters.segments.map(i => i.segmentDesc).join(', '),
    timeBand: filters.timeBand.join(', '),
    hourRange: filters.hourRange.join(', '),
    startAccountingDate: filters.startAccountingDate.accept,
    endAccountingDate: filters.endAccountingDate.accept,
    startOperationDate: filters.startOperationDate.accept,
    endOperationDate: filters.endOperationDate.accept,
    startBusinessDate: filters.startBusinessDate.accept,
    endBusinessDate: filters.endBusinessDate.accept,
    regionalMgmt: opsFilters.regionalMgmt.map(i => i.regionalMgmt).join(', '),
    region: opsFilters.region.map(i => i.region).join(', '),
    management: opsFilters.management.map(i => i.mgmt).join(', '),
    supervision: opsFilters.supervision.map(i => i.supervision).join(', '),
    agreement: opsFilters.agreement.map(i => i.agreementType).join(', '),
    company: opsFilters.company.map(i => i.companyName).join(', '),
    groupBy: filters.groupBy.map(i => t(`toolbar.groupBy.${i}`)).join(', '),
  },
];
*/

function* download({ downloadHandler, errorMessage }: DownloadRequestedAction) {
  try {
    yield downloadHandler();
    yield put(downloadSuccess());
  } catch (error) {
    yield put(downloadFailure({ error }));
    yield put(
      enqueueNotification({
        key: 'downloadError',
        message: errorMessage,
        variant: 'error',
      })
    );
  }
}

function* pushMissingClosingTable({
  tablesExport,
  locale,
  t,
}: DownloadReportPdfAction & { tablesExport: TablesExportPdf }) {
  yield put(findClosingSalesRequested());

  const {
    success,
    failure,
  }: {
    success?: FindClosingSalesSuccessAction;
    failure?: FindClosingSalesFailureAction;
  } = yield race({
    success: take(ReportActionType.findClosingSalesSuccess),
    failure: take(ReportActionType.findClosingSalesFailure),
  });

  if (success) addMissings(tablesExport, success.closing, t, locale);
  // eslint-disable-next-line no-console
  if (failure) console.log('Falló el export de missing closing sales', failure);
}

function* downloadReportPdfHandler(action: DownloadReportPdfAction) {
  const {
    includeMissingClosingTable,
    title,
    table,
    parameters,
    opStructures,
    t,
    locale,
    pdfReportType,
    extraFilters,
    additionalParameters,
  } = action;
  const selectedFilters: ReportFilter = yield select(reportFilterSelector);
  const filters = action.filters ?? selectedFilters;
  const opsFilters: OpStructuresFilter = yield select(reportOpStructuresFilterSelector);
  const userInfo: ReturnType<typeof userInfoSelector> = yield select(userInfoSelector);
  const tablesExport = new TablesExportPdf();

  const requestedAt = new Date().toLocaleString();
  const userParts = [userInfo?.name, userInfo?.username].filter(Boolean) as string[];
  const userText = userParts
    .filter((value, index, list) => index === 0 || value !== list[0])
    .join(' - ');
  const footerParts = [
    userText ? t('shared.pdfExport.footerUser', { user: userText }) : '',
    t('shared.pdfExport.footerDate', { date: requestedAt }),
  ].filter(Boolean);
  const footerText = footerParts.join(' | ');
  const pageTemplate = t('shared.pdfExport.pageOf');

  if (parameters?.length)
    tablesExport.pushTable(
      'parameters',
      getParametersTable({
        filters,
        opsFilters,
        t,
        title,
        locale,
        parameters,
        opStructures,
        pdfReportType,
        extraFilters,
        additionalParameters,
      })
    );

  if (includeMissingClosingTable) {
    yield pushMissingClosingTable({ tablesExport, ...action });
  }

  tablesExport.pushTable('table', table);
  tablesExport.export(title, footerText, pageTemplate);
}

function* downloadReportPdf(action: DownloadReportPdfAction) {
  yield put(
    downloadRequested({
      downloadHandler: () => downloadReportPdfHandler(action),
      errorMessage: t => t('errors.exports.body', { type: 'PDF' }),
    })
  );
}

export default [
  takeLatest(ActionType.downloadRequested, download),
  takeLatest(ActionType.downloadReportPdf, downloadReportPdf),
];
